import {concealUnopenedPatches, type PatchConcealmentReason} from './patchVisibility.js';
import {PatchLevel} from './patchLevels';
import { guestRequest,GuestApiError } from "./guestApi.js";
import {db,workspaceAccount} from './db.js';
import {getAuthToken} from './auth.js';
export interface BadgeRule {
  type: "visit_count" | "poi_arrival" | "project_count" | "work_minutes" | "photo_count" | "manual" | "project_completed" | "route_recorded" | "route_published" | "season_count" | "season_visits" | "place_count" | "year_count";
  project_id?: string;
  season_mode?: 'occurrences' | 'types';
  season?: 'winter' | 'spring' | 'summer' | 'fall';
  timezone?: 'America/Chicago';
  enabled_at?: string;
  threshold_minutes?: number;
  count?: number;
  poi_id?: string;
  radius?: number;
  dwell_seconds?: number;
}
export interface PatchProgressionLevel {
  number: number;
  description: string;
  image: string;
  rarity: PatchLevel;
  rule: BadgeRule;
}
export interface BadgeDefinition {
  progression_levels?: PatchProgressionLevel[] | null;
  progression_level?: number | null;
  hidden?: boolean;
  concealment_reason?: PatchConcealmentReason;
  level?: PatchLevel;
  badge_id: string;
  farm_id: string;
  name: string;
  description: string;
  image: string;
  presentation_kind?: 'patch' | 'medal';
  story?: string;
  points: number;
  status: "draft" | "published" | "retired";
  rule: BadgeRule;
  version: number;
  published_at?: string | null;
}
export interface BadgeAward {
  progression_level?: number | null;
  milestone?: string;
  earned_on?:string|null;
  display_name?: string;
  level?: PatchLevel;
  display_image?: string;
  award_id: string;
  badge_id: string;
  name: string;
  description: string;
  image: string;
  presentation_kind?: 'patch' | 'medal';
  story?: string;
  points: number;
  awarded_at: string;
  revoked_at: string | null;
  acknowledged_at: string | null;
  historical: boolean;
  rule_version: number;
  source:
    | {type:'project_completed'|'route_recorded'|'route_published'|'season_count'|'season_visits'|'place_count'|'year_count';count:number;sources:Record<string,unknown>[]}
    | {type:'onboarding'}
    | {type:'manual';actor_email:string;reason:string}
    | { type: "visit_count"; count: number; visit_ids: string[] }
    | { type: "poi_arrival"; poi_id: string; reached_at: string }
    | { type: "project_count" | "work_minutes" | "photo_count"; count:number; seconds?:number; sources:{id?:string;photo_id?:string;project_id?:string;revision:number}[] };
}
export interface BadgeState {
  offlineSavedAt?:string;
  hidden_images?:Partial<Record<PatchLevel,string>>;
  back_images?:Partial<Record<PatchLevel,string>>;
  back_image?:string;
  unearned_image?:string;
  definitions: BadgeDefinition[];
  awards: BadgeAward[];
  statistics?: BadgeRarity[];
  points: number;
  leaderboard_visible: boolean;
  adjustments: {
    adjustment_id: string;
    award_id: string;
    actor_email: string;
    action: string;
    reason: string;
    created_at: string;
  }[];
}
export interface BadgeRarity {badge_id:string;earned_count:number;member_count:number;earned_percent:number}
export interface SharedBadge {progression_level?:number|null;level?:PatchLevel;badge_id:string;name:string;image:string;presentation_kind?:'patch'|'medal';hidden?:boolean;concealment_reason?:PatchConcealmentReason}
export interface BadgeDetailState {
 levels?:{number:number;name:string;image:string;description:string;rarity:PatchLevel;earned:boolean;earned_at:string|null;earned_on:string|null}[];
 earned_at?:string|null;
 earned_on?:string|null;
 hidden_images?:Partial<Record<PatchLevel,string>>;
 unearned_image?:string;
 badge:SharedBadge&{description?:string;story?:string};earned:boolean;statistics:BadgeRarity;
 recipients:{id:string;name:string;badges:SharedBadge[]}[];
}
export interface SharedPatchCollection {
 name:string;
 badges:(SharedBadge&{awarded_at:string;earned_on?:string|null})[];
 hidden_images?:Partial<Record<PatchLevel,string>>;
 back_images?:Partial<Record<PatchLevel,string>>;
 unearned_image?:string;
 back_image?:string;
}
export const badgeCacheKey=(farm:string)=>`badge-cache:${farm}`;
const accessScope=(scope?:string)=>scope?.split(':photo-access-v1:')[0];
export const badgeRequest = async <T>(
  farm: string,
  path = "",
  method = "GET",
  body?: unknown,
  query: Record<string, string> = {},
  options:{requireOfflineCopy?:boolean}={},
) => {
  const database=db,account=workspaceAccount,token=getAuthToken();
  const own=method==='GET'&&path===''&&Object.keys(query).length===0;
  const current=()=>database===db&&account===workspaceAccount&&token===getAuthToken();
  const startedScope=own?accessScope((await database.syncState.get('global_sync'))?.scope):undefined;
  if(!current())throw new Error('Account changed. Reopen your patches.');
  let data:T;
  try{data=await guestRequest<T>(
    `/guest/badges${path}?${new URLSearchParams({ ...query, farm_id: farm })}`,
    method,
    body,
  );}catch(error){
    if(own&&current()&&error instanceof GuestApiError&&[403,404].includes(error.status))await database.workspaceMeta.delete(badgeCacheKey(farm)).catch(()=>{});
    throw error;
  }
  if(!current())throw new Error('Account changed. Reopen your patches.');
  // Only the signed-in recipient's own response is saved. Admin/other-member
  // requests and recognition delivery retain their live-only semantics.
  if(own&&account&&Array.isArray((data as BadgeState)?.awards)&&Array.isArray((data as BadgeState)?.definitions)){
    data=concealUnopenedPatches(data as BadgeState) as T;
    const write=database.transaction('rw',[database.workspaceMeta,database.syncState],async()=>{
      const latestScope=accessScope((await database.syncState.get('global_sync'))?.scope);
      if(!current())throw new Error('Account changed. Reopen your patches.');
      if(startedScope&&startedScope!==latestScope)throw new GuestApiError('Property access changed. Reopen your patches.',403);
      await database.workspaceMeta.put({key:badgeCacheKey(farm),value:{data,savedAt:new Date().toISOString(),accessScope:latestScope}});
    });
    if(options.requireOfflineCopy)await write;else await write.catch(error=>{if(error instanceof GuestApiError)throw error;});
  }else if(method!=='GET'&&path!=='/acknowledge')await database.workspaceMeta.delete(badgeCacheKey(farm)).catch(()=>{});
  if(!current())throw new Error('Account changed. Reopen your patches.');
  return data;
};

/** Explicit saved-copy reading for collection/Profile, never award reveals. */
export async function loadOwnBadgeState(farm:string):Promise<BadgeState>{
  const database=db,account=workspaceAccount,token=getAuthToken();
  const current=()=>database===db&&account===workspaceAccount&&token===getAuthToken();
  if(navigator.onLine)try{return await badgeRequest<BadgeState>(farm);}catch(error){
    if(!current())throw error;
    if(error instanceof GuestApiError&&![408,429,500,502,503,504].includes(error.status))throw error;
  }
  const saved=(await database.workspaceMeta.get(badgeCacheKey(farm)))?.value;
  const latestScope=accessScope((await database.syncState.get('global_sync'))?.scope);
  if(!current())throw new Error('Account changed. Reopen your patches.');
  if(saved?.accessScope&&saved.accessScope!==latestScope){await database.workspaceMeta.delete(badgeCacheKey(farm));throw new GuestApiError('Property access changed. Reopen your patches.',403);}
  if(!account||!saved?.data)throw new Error('Your patches are not saved on this phone yet. Open Patches or Prepare for Visit while connected.');
  return {...concealUnopenedPatches(saved.data),offlineSavedAt:saved.savedAt};
}
